import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initPlatform, isNative, setInitialLanguage } from './utils/Device';
import Boundary from './utils/Boundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import defaultApplication from './assets/application.json';
import { getContent } from './utils/ContentStore';
import { primeBaseContent } from './utils/BaseContent';
import { clearTrying } from './utils/ContentFiles';
import { primeCatalogFromDownloads } from './utils/LanguageCatalog';
import { primePersistedState } from './utils/Persist';
import { startWindowGeometry } from './utils/WindowGeometry';
import languages from './utils/LanguageCatalog';

window.onerror = (message, source, lineno, colno, error) => {
  console.error('Global error caught:', { message, source, lineno, colno, error });
  return false;
};

window.onunhandledrejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason);
};

const root = ReactDOM.createRoot(document.getElementById('root'));

const signalReactReady = () => {
  const emitReady = () => {
    window.dispatchEvent(new Event('app:react-ready'));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(emitReady);
    });
    return;
  }

  window.setTimeout(emitReady, 0);
};

const CrashGuard = ({ direction, title, message, reloadLabel }) => (
  <div
    dir={direction}
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-b from-cyan-500 to-sky-500 px-5 py-6 select-none"
  >
    <div className="w-[min(780px,92vw)] rounded-md border border-sky-100/40 bg-sky-950/70 p-7 md:p-10 shadow-2xl backdrop-blur-md">
      <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full border border-sky-100/60 bg-sky-100/10 text-3xl font-black leading-none text-neutral-100">!</div>
      <div className="mb-3 text-2xl md:text-4xl font-bold tracking-tight text-neutral-100">
        {title}
      </div>
      <div className="mb-7 text-base md:text-xl leading-relaxed text-neutral-100/90">
        {message}
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md border border-neutral-100 bg-neutral-100 px-5 py-3 text-sm md:text-base font-semibold text-sky-950 transition-colors duration-200 hover:bg-transparent hover:text-neutral-100"
      >
        {reloadLabel}
      </button>
    </div>
  </div>
);

const getLanguageCandidates = (lang) => {
  const normalized = (lang || '').toString().trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  const base = normalized.split('-')[0];
  return Array.from(new Set([normalized, base].filter(Boolean)));
};

const loadBootTranslationBundle = async (language, preloadPlan) => {
  const normalizedLanguage = (language || '').toLowerCase();
  if (!normalizedLanguage || normalizedLanguage === 'en') {
    return null;
  }

  const shouldLoad = (flag) => Boolean(preloadPlan?.[flag]);
  const loadOptional = (flag, importer) => (
    shouldLoad(flag) ? importer().catch(() => null) : Promise.resolve(null)
  );

  const [
    translatedQuran,
    translatedCover,
    translatedIntro,
    translatedAppendix,
    translatedApplication,
  ] = await Promise.all([
    // The big files come from /content as plain JSON; cover and application stay in the
    // bundle because they are a few kilobytes and needed before the first paint.
    loadOptional('loadQuran', () => getContent('quran', normalizedLanguage)),
    loadOptional('loadCover', () => import(
      /* webpackInclude: /cover_[a-z0-9-]+\.json$/ */
      `./assets/translations/${normalizedLanguage}/cover_${normalizedLanguage}.json`
    )),
    loadOptional('loadIntro', () => getContent('introduction', normalizedLanguage)),
    loadOptional('loadAppendices', () => getContent('appendices', normalizedLanguage)),
    import(
      /* webpackInclude: /application_[a-z0-9-]+\.json$/ */
      `./assets/translations/${normalizedLanguage}/application_${normalizedLanguage}.json`
    ).catch(() => null),
  ]);

  return {
    language: normalizedLanguage,
    translation: translatedQuran || null,
    coverData: translatedCover?.default || null,
    introduction: translatedIntro || null,
    appendices: translatedAppendix || null,
    map: null,
    application: translatedApplication?.default || null,
    preloadPlan: preloadPlan || null,
  };
};

const getRuntimePath = () => {
  const basePath = process.env.PUBLIC_URL || '';
  const currentPathname = window.location.pathname || '/';

  if (basePath && basePath !== '/' && currentPathname.startsWith(basePath)) {
    const stripped = currentPathname.slice(basePath.length);
    if (!stripped) {
      return '/';
    }
    return stripped.startsWith('/') ? stripped : `/${stripped}`;
  }

  return currentPathname || '/';
};

const isRootRoutePath = (path) => {
  const segments = (path || '/').split('/').filter(Boolean);

  if (segments.length === 0) {
    return true;
  }

  if (segments.length === 1 && segments[0] === 'search') {
    return true;
  }

  if (segments.length === 2 && segments[0] === 'appendix') {
    return true;
  }

  if (segments.length === 2 && segments[1] === 'search') {
    return true;
  }

  if (segments.length === 3 && segments[1] === 'appendix') {
    return true;
  }

  return false;
};

const isSearchRoutePath = (path) => /(?:^|\/)search\/?$/.test(path || '/');

const isAppendixRoutePath = (path) => /(?:^|\/)appendix\/\d+\/?$/.test(path || '/');

const getSavedPage = () => {
  const parsed = parseInt(localStorage.getItem('qurantft-pn') || '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getRootBootPreloadPlan = (runtimePath) => {
  const savedPage = getSavedPage();
  const hasSavedPage = savedPage !== null;
  const isSearch = isSearchRoutePath(runtimePath);
  const isAppendix = isAppendixRoutePath(runtimePath);
  const shouldShowCover = !hasSavedPage && !isSearch && !isAppendix && !process.env.REACT_APP_DEFAULT_LANG;

  let effectivePage = hasSavedPage ? savedPage : 1;
  if (isAppendix) {
    effectivePage = 397;
  }

  return {
    // If cover is visible at startup, defer heavy quran translation load to post-cover flow.
    loadQuran: !shouldShowCover,
    loadCover: shouldShowCover,
    loadIntro: effectivePage <= 22,
    loadAppendices: effectivePage >= 396,
  };
};

const getInitialRouteConfig = () => {
  const runtimePath = getRuntimePath();

  if (isRootRoutePath(runtimePath)) {
    return {
      loader: () => import(/* webpackChunkName: "route-root" */ './pages/Root'),
    };
  }

  return {
    loader: () => import(/* webpackChunkName: "route-leaf" */ './pages/Leaf'),
  };
};

const preloadInitialRouteChunk = async () => {
  const routeConfig = getInitialRouteConfig();
  await routeConfig.loader();
};

const renderApp = async () => {
  let loadingDirection = 'ltr';
  let bootData = null;
  let errorTitle = defaultApplication.errorTitle || 'Something went wrong';
  let errorMessage =
    defaultApplication.errorMessage || 'An unexpected error occurred. Refresh to continue.';
  let errorReloadLabel = defaultApplication.errorReload || 'Refresh';

  try {
    await initPlatform();
    // The system regions over the window are asked for now, so they are known by the first screen.
    startWindowGeometry();
    // What the reader left behind comes back from the app's own file before a single screen
    // reads it, because the web view's own store cannot be trusted to still hold it.
    await primePersistedState();
    // A language that arrived with a content update joins the catalog before anything reads it.
    await primeCatalogFromDownloads();
    const initializedLang = await setInitialLanguage();

    const languageCandidates = getLanguageCandidates(
      initializedLang || localStorage.getItem('lang') || process.env.REACT_APP_DEFAULT_LANG || 'en'
    );
    const resolvedLang = languageCandidates.find((lang) => languages[lang]) || 'en';
    loadingDirection = languages[resolvedLang]?.dir || 'ltr';

    const runtimePath = getRuntimePath();
    const preloadPlan = isRootRoutePath(runtimePath)
      ? getRootBootPreloadPlan(runtimePath)
      : { loadQuran: true, loadCover: false, loadIntro: false, loadAppendices: false };

    const runtimeCacheReadyPromise = (async () => {
      try {
        const { ensureRuntimeCachesReady } = await import('./utils/Generator');
        await ensureRuntimeCachesReady({
          languages: [resolvedLang],
          startupBlocking: true,
        });
      } catch (cacheError) {
        // Fail-open: keep startup resilient if cache build fails.
        console.error('Startup runtime cache build failed; continuing with fail-open policy', cacheError);
      }
    })();

    // A last resort, not a budget: the cache build is normally over in well under a second and a
    // database that stops answering is already handled where it is opened. This only makes sure
    // that nothing can leave the app on its splash screen for ever.
    const startupCacheDeadline = new Promise((resolve) => setTimeout(resolve, 20000));

    const [preloadedBootData] = await Promise.all([
      loadBootTranslationBundle(resolvedLang, preloadPlan),
      // The English base text is what every screen falls back to, so it is ready before render.
      primeBaseContent(),
      preloadInitialRouteChunk(),
      Promise.race([runtimeCacheReadyPromise, startupCacheDeadline]),
    ]);

    bootData = preloadedBootData;

    if (bootData?.application) {
      errorTitle = bootData.application.errorTitle || errorTitle;
      errorMessage = bootData.application.errorMessage || errorMessage;
      errorReloadLabel = bootData.application.errorReload || errorReloadLabel;
    }
  } catch (error) {
    console.error('Bootstrap failed', error);
  }

  root.render(
    <React.StrictMode>
      <Boundary
        fallback={
          <CrashGuard
            direction={loadingDirection}
            title={errorTitle}
            message={errorMessage}
            reloadLabel={errorReloadLabel}
          />
        }
      >
        <App loadingDirection={loadingDirection} bootData={bootData} />
      </Boundary>
    </React.StrictMode>
  );
  signalReactReady();

  // Once per cold start, after the screen is up: look for newer text in the background and keep
  // it for the next start. Never in the way of reading, never retried within this session.
  window.setTimeout(() => {
    // The app got this far, so whatever it read at start works; the mark can go.
    void clearTrying();
    import('./utils/ContentUpdater')
      .then(({ runContentUpdateOnce }) => runContentUpdateOnce({ language: localStorage.getItem('lang') || 'en' }))
      .catch(() => null);
  }, 5000);

  if (isNative()) {
    serviceWorkerRegistration.unregister();
    return;
  }

  serviceWorkerRegistration.register();
};

renderApp();

reportWebVitals();
